/*
 * ==========================================================================
 * SingRank - Meta Pixel tracker for Ablink's custom-coded pages   v1.6.0
 * --------------------------------------------------------------------------
 * WHY THIS EXISTS
 * Ablink's real inventory and its whole enquiry funnel are custom-built and
 * rendered client-side from Ablink's own backend APIs. None of it is a Shopify
 * product page, so Shopify's Facebook & Instagram pixel never sees any of it.
 * This file feeds Meta the real thing: which vehicle a visitor looked at, what
 * type it is, what they searched for, and when they actually asked for a quote.
 *
 * COVERAGE
 *   /pages/new-commercial-vehicles   52 new vehicles    cards .abl-card
 *   /pages/car-marketplace          132 used vehicles   cards .t8-card
 *   /pages/motor-insurance          service page        insurance intent
 *   /pages/coe-renewal-loan-...     loan calculator     COE renewal intent
 *   every page                      ABLWA WhatsApp funnel
 *
 * EVENTS
 *   VehicleView         trackCustom  click on any vehicle card or More Info
 *   ServiceInterest     trackCustom  insurance page, or a WhatsApp funnel mode
 *   COERenewalInterest  trackCustom  COE category / term / expiry chosen
 *   Search              track        catalogue search box or filter used
 *   Contact             track        WhatsApp opened - reached out, not yet enquired
 *   Lead                track        enquiry form actually submitted
 *
 * WHAT CHANGED IN v1.6.0 - and why
 * A click on a vehicle now sends Meta's STANDARD ViewContent as well as our
 * own VehicleView. Nothing was removed. Reason: audiences can be built from
 * custom events, but Advantage+ catalog ads cannot - Meta matches a person to
 * a catalogue item only through ViewContent / AddToCart / Purchase carrying
 * content_type 'product'. Without this line the dynamic campaign promised in
 * the client guide runs and spends while showing vehicles nobody looked at,
 * and Meta reports no error at all. The ids already match the catalogue feed.
 *
 * WHAT CHANGED IN v1.5.0 - and why
 * 1. Contact and Lead are no longer the same event. Until now, opening
 *    WhatsApp and submitting the quote form both fired Lead, so the Lead
 *    count read higher than the real enquiry count and the ads team could
 *    not tell a browser from a buyer. Opening WhatsApp is now Contact.
 *    Lead is reserved for "Request Professional Quote", which is the only
 *    action that carries a name, an email and a budget.
 * 2. Search now exists. Both inventory pages filter client-side, so nothing
 *    navigates and Meta previously heard nothing at all about what a visitor
 *    was looking for. One debounced Search event now carries the typed query,
 *    the active filters and how many units came back.
 * Nothing else was touched. VehicleView, ServiceInterest and
 * COERenewalInterest behave exactly as they did in v1.4.0.
 *
 * DATA SOURCE - every value comes from Ablink's own data, nothing invented.
 * The card badge IS the raw `category` string from the API. Powertrain is read
 * out of that string first, then out of the vehicle's own name/description -
 * but only for words that literally state it (ELECTRIC, KWH, HYBRID, DIESEL,
 * PETROL). That is data, not an inference about a model.
 *
 * USED VEHICLES NEED A LOOKUP. The card only prints the short model name
 * ("TOYOTA ALPHARD"), while the word HYBRID lives in the API's `description`
 * ("TOYOTA ALPHARD ELEGANCE HYBRID"). The record id sits inside the card's own
 * onclick, `t8OpenEnq_...('129')`, so this file fetches /api/pricelist once and
 * matches on that id. Measured 2026-08-25: 131 of 131 cards matched, 0 missed,
 * and it lifts Hybrid from 1 to 14. Without the lookup those 13 would be sent
 * to Meta as Unknown. If the fetch fails the reader falls back to the short
 * name and simply reports Unknown - it never blocks and never guesses.
 *
 * THE USED API HAS NO FUEL FIELD AT ALL. Re-verified 2026-08-26 against the
 * live endpoint: `fuel_type` is not merely blank, the key is absent from every
 * one of the 131 records. (An earlier note in this header said "empty on all
 * 131", which was wrong and would have sent someone hunting for blank values.)
 * So the category string and the description are the only fuel signals here,
 * and 35 units state no fuel anywhere. Adding a fuel column to the marketplace
 * backend would classify them with no change to this file.
 *
 * DO NOT split the category on a dash. Ablink uses the dash as a size range in
 * "HEAVY VEHICLE 14FT - 17FT" and "REFRIGERATED 10FT - 24FT", while
 * "ELECTRIC VAN" and "BUS DIESEL" carry no separator at all. Scan the whole
 * string for fuel keywords instead, which is what powertrainOf does.
 *
 * ELEMENT IDS ARE NOT STABLE. Every filter control on both pages carries the
 * Shopify section id inside its own id, for example
 * `t8-category-template--26228055408955__inventory_listing_MULYJL`. Match on
 * the class, or on an id PREFIX. Never hard-code a full id here.
 *
 * VERIFIED 2026-08-26 against the live DOM and the live APIs:
 *   new-commercial-vehicles  52 cards, 52 deep links, 0 blank price,
 *                            0 unknown fuel. Diesel 25 / Electric 23 / Petrol 4
 *   car-marketplace         132 cards, 132 payloads, 105 with COE expiry,
 *                           Diesel 68 / Hybrid 14 / Petrol 11 / Electric 4 /
 *                           Unknown 35, and 28 priced 0 (the POA units).
 *
 * SAFETY
 * - Renders nothing. No layout, content or styling is touched.
 * - Listeners are passive and never call preventDefault, so they cannot block
 *   a click, a modal, or a WhatsApp hand-off.
 * - Capture phase, so the event is sent before WhatsApp navigation starts.
 * - Every path is wrapped in try/catch; on failure the page is unaffected.
 * - De-duplicated per subject per page view.
 * - No regular expressions and no escape sequences, by house rule.
 *
 * DEBUG
 *   window.SR_META_TRACKER.fired     everything sent this page view
 *   window.SR_META_TRACKER.read(el)  read one card without sending
 *   window.SR_META_TRACKER.readSearch()  read the filter state without sending
 *
 * Code by SingRank (https://singrank.com) for AB Link Pte Ltd. Created 2026-08-25. Updated 2026-08-26.
 * ==========================================================================
 */
(function () {
  'use strict';

  if (window.SR_META_TRACKER) { return; }

  var API = { version: '1.6.0', fired: [], read: null, readSearch: null };
  window.SR_META_TRACKER = API;

  var FIRED = {};
  var MAX_FBQ_TRIES = 40;
  var COE_SETTLE_MS = 1500;
  var SEARCH_SETTLE_MS = 900;
  var USED_API = 'https://kk4080kgosksskkkow4gw4o0.34.21.196.238.sslip.io/api/pricelist';
  var USED_INDEX = null;

  // Meta's own standard events go through track(). Everything else is ours
  // and goes through trackCustom(), so we never collide with the app pixel.
  var STANDARD_EVENTS = { Lead: true, Contact: true, Search: true };

  /* ---------- helpers, no regex ---------- */

  function txt(root, sel) {
    try { var e = root.querySelector(sel); return e ? String(e.textContent).trim() : ''; }
    catch (e) { return ''; }
  }

  function up(s) { return String(s == null ? '' : s).toUpperCase(); }

  function has(hay, needle) { return up(hay).indexOf(needle) > -1; }

  function money(s) {
    s = String(s == null ? '' : s);
    var out = '';
    for (var i = 0; i < s.length; i++) {
      var c = s.charAt(i);
      if ((c >= '0' && c <= '9') || c === '.') { out += c; }
    }
    return out ? parseFloat(out) : 0;
  }

  function slugOf(href) {
    if (!href) { return ''; }
    var h = String(href);
    var i = h.indexOf('v=');
    if (i < 0) { return ''; }
    var rest = h.substring(i + 2);
    var amp = rest.indexOf('&');
    if (amp > -1) { rest = rest.substring(0, amp); }
    return rest;
  }

  function val(id) {
    try { var e = document.getElementById(id); return e && e.value ? String(e.value).trim() : ''; }
    catch (e) { return ''; }
  }

  function classHas(el, token) {
    try {
      if (!el || !el.className) { return false; }
      return String(el.className).indexOf(token) > -1;
    } catch (e) { return false; }
  }

  function idStartsWith(el, prefix) {
    try { return !!(el && el.id && String(el.id).indexOf(prefix) === 0); }
    catch (e) { return false; }
  }

  // The used-vehicle card carries its record id inside its own onclick,
  // for example t8OpenEnq_template__xxx('129').
  function recordIdOf(card) {
    try {
      var oc = card.getAttribute('onclick') || '';
      var i = oc.indexOf("('");
      if (i < 0) { return ''; }
      var rest = oc.substring(i + 2);
      var j = rest.indexOf("'");
      if (j < 0) { return ''; }
      return rest.substring(0, j);
    } catch (e) { return ''; }
  }

  function loadUsedIndex() {
    try {
      if (USED_INDEX !== null) { return; }
      USED_INDEX = {};
      if (!window.fetch) { return; }
      window.fetch(USED_API, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          var items = (j && j.data) ? j.data : [];
          for (var i = 0; i < items.length; i++) {
            var it = items[i];
            if (it && it.id != null) { USED_INDEX[String(it.id)] = it; }
          }
        })
        .catch(function () {});
    } catch (e) {}
  }

  /* ---------- taxonomy, from Ablink's own strings ----------
     Category wins. The name is consulted only for words that literally state
     the powertrain, so nothing here is an inference about a model. */

  function powertrainOf(category, name) {
    var c = up(category);
    if (has(c, 'ELECTRIC')) { return ['Electric', 'category']; }
    if (has(c, 'HYBRID'))   { return ['Hybrid',   'category']; }
    if (has(c, 'DIESEL'))   { return ['Diesel',   'category']; }
    if (has(c, 'PETROL'))   { return ['Petrol',   'category']; }

    var n = up(name);
    if (has(n, 'ELECTRIC') || has(n, 'KWH')) { return ['Electric', 'name']; }
    if (has(n, 'HYBRID'))                    { return ['Hybrid',   'name']; }
    if (has(n, 'DIESEL'))                    { return ['Diesel',   'name']; }
    if (has(n, 'PETROL'))                    { return ['Petrol',   'name']; }

    return ['Unknown', 'none'];
  }

  function bodyOf(category) {
    var c = up(category);
    if (has(c, 'PRIMEMOVER') || has(c, 'PRIME MOVER')) { return 'Prime Mover'; }
    if (has(c, 'TIPPER'))        { return 'Tipper Truck'; }
    if (has(c, 'CRANE'))         { return 'Crane Truck'; }
    if (has(c, 'PICKUP'))        { return 'Pickup Truck'; }
    if (has(c, 'REFRIGERAT'))    { return 'Refrigerated Truck'; }
    if (has(c, 'BUS'))           { return 'Bus'; }
    if (has(c, 'VAN'))           { return 'Van'; }
    if (has(c, 'LORRY'))         { return 'Lorry'; }
    if (has(c, 'HEAVY VEHICLE')) { return 'Heavy Vehicle'; }
    if (has(c, 'CAR'))           { return 'Car'; }
    if (has(c, 'TRUCK'))         { return 'Truck'; }
    return 'Vehicle';
  }

  /* ---------- card readers ---------- */

  function readNewVehicleCard(card) {
    var category = txt(card, '.abl-cat-pill');
    var name     = txt(card, '.abl-make');
    var info     = card.querySelector('.abl-btn-info');
    var slug     = info ? slugOf(info.getAttribute('href')) : '';
    var pt       = powertrainOf(category, name);
    var body     = bodyOf(category);

    return {
      content_ids:       [slug || name],
      content_type:      'vehicle',
      content_name:      name,
      content_category:  pt[0] + ' ' + body,
      powertrain:        pt[0],
      powertrain_source: pt[1],
      vehicle_category:  category,
      body_type:         body,
      vehicle_condition: 'New',
      value:             money(txt(card, '.abl-price')),
      currency:          'SGD',
      source_page:       'new-commercial-vehicles'
    };
  }

  function readUsedVehicleCard(card) {
    var category = txt(card, '.t8-card-badge');
    var name     = txt(card, '.t8-card-name') || card.getAttribute('aria-label') || '';
    var rid      = recordIdOf(card);
    var rec      = (USED_INDEX && rid) ? (USED_INDEX[rid] || null) : null;

    // The card prints the short model name; the record carries the full
    // description, which is where a word like HYBRID actually appears.
    var fullName = (rec && rec.description) ? String(rec.description) : name;
    if (rec && rec.category) { category = String(rec.category); }

    var pt    = powertrainOf(category, fullName);
    var body  = bodyOf(category);
    var price = money(txt(card, '.t8-card-price'));
    if (!price && rec && rec.asking_price) { price = money(rec.asking_price); }

    var out = {
      content_ids:       [rid || name],
      content_type:      'vehicle',
      content_name:      name,
      content_category:  pt[0] + ' ' + body,
      powertrain:        pt[0],
      powertrain_source: pt[1],
      vehicle_category:  category,
      body_type:         body,
      vehicle_condition: 'Used',
      value:             price,
      currency:          'SGD',
      source_page:       'car-marketplace'
    };

    if (rec && rec.description) { out.vehicle_description = String(rec.description); }
    if (rec && rec.coe_expiry)  { out.coe_expiry = String(rec.coe_expiry); }

    return out;
  }

  function readCard(card) {
    try {
      if (!card) { return null; }
      if (card.classList && card.classList.contains('t8-card')) { return readUsedVehicleCard(card); }
      return readNewVehicleCard(card);
    } catch (e) { return null; }
  }

  API.read = readCard;

  /* ---------- sending ---------- */

  function fire(eventName, data) {
    try {
      if (!data || !data.content_name) { return; }

      // The key carries the COE choices and the search state too, so a visitor
      // who refines a filter sends a fuller event instead of being deduped.
      var key = eventName + '::' + data.content_name + '::' + (data.content_category || '')
              + '::' + (data.coe_category || '') + '::' + (data.coe_term || '')
              + '::' + (data.filters || '');
      if (FIRED[key]) { return; }
      FIRED[key] = true;

      API.fired.push({ event: eventName, data: data });

      var tries = 0;
      (function attempt() {
        if (typeof window.fbq !== 'function') {
          tries = tries + 1;
          if (tries > MAX_FBQ_TRIES) { return; }
          window.setTimeout(attempt, 250);
          return;
        }
        try {
          if (STANDARD_EVENTS[eventName]) { window.fbq('track', eventName, data); }
          else { window.fbq('trackCustom', eventName, data); }

          // v1.6.0 - the catalogue half. Advantage+ catalog (dynamic) ads only
          // ever match a person to a product through Meta's STANDARD ViewContent
          // event carrying content_type 'product'. Our VehicleView is trackCustom
          // and says content_type 'vehicle', which belongs to Meta's Vehicles
          // vertical, not to the e-commerce catalogue the feed uploads into. So
          // dynamic ads used to run, spend, and quietly show the wrong vehicle -
          // with no error anywhere. A real click now ALSO sends the standard
          // event, using the same ids the feed carries (new = page slug, used =
          // numeric listing id), so the two finally match.
          //
          // Only VehicleView does this. An impression is not a view, and firing
          // ViewContent on scroll would inflate the count and poison the
          // audience. The rich parameters stay on the custom event, which is
          // what the interest audiences are built on - nothing is replaced here.
          //
          // No double-count risk on /products/*: those are Shopify pages, this
          // file only reads .abl-card and .t8-card, which exist nowhere else.
          if (eventName === 'VehicleView' && data.content_ids && data.content_ids[0]) {
            window.fbq('track', 'ViewContent', {
              content_ids:      data.content_ids,
              content_type:     'product',
              content_name:     data.content_name,
              content_category: data.content_category,
              value:            data.value || 0,
              currency:         'SGD'
            });
          }
        } catch (e) {}
      })();
    } catch (e) {}
  }

  function servicePayload(service, extra) {
    var d = {
      content_name:     service,
      content_category: service,
      content_type:     'service',
      service:          service,
      source_page:      window.location.pathname,
      currency:         'SGD'
    };
    if (extra) { for (var k in extra) { if (extra.hasOwnProperty(k)) { d[k] = extra[k]; } } }
    return d;
  }

  /* ---------- the ABLWA WhatsApp funnel, present on every page ----------
     'interest' = picked a lane in the widget, nothing sent yet.
     'contact'  = WhatsApp is being opened. Reaching out, not an enquiry.
     'lead'     = the quote form was actually submitted, with contact details. */

  function ablwaIntent(el) {
    var oc = '';
    try { oc = el.getAttribute('onclick') || ''; } catch (e) { return null; }
    if (!oc || oc.indexOf('ABLWA') < 0) { return null; }
    if (oc.indexOf("pickMode('new')")  > -1) { return { kind: 'interest', service: 'New Vehicles' }; }
    if (oc.indexOf("pickMode('used')") > -1) { return { kind: 'interest', service: 'Used Vehicles' }; }
    if (oc.indexOf("pickMode('coe')")  > -1) { return { kind: 'interest', service: 'COE Renewal' }; }
    if (oc.indexOf('directChat')       > -1) { return { kind: 'contact',  service: 'Direct WhatsApp Chat' }; }
    if (oc.indexOf('coeContinue')      > -1) { return { kind: 'contact',  service: 'COE Renewal Enquiry' }; }
    if (oc.indexOf('send')             > -1) { return { kind: 'lead',     service: 'Professional Quote Request' }; }
    return null;
  }

  function coeContext() {
    var out = {};
    var cat = val('coe_cat');
    if (cat) { out.coe_category = cat; }
    var term = val('coe_term');
    if (term) { out.coe_term = term; }
    var expiry = val('coe_expiry');
    if (expiry) { out.coe_expiry = expiry; }
    var budget = val('wa_budget');
    if (budget) { out.value = money(budget); }
    return out;
  }

  /* ---------- catalogue search and filters ----------
     Both inventory pages filter in the browser, so nothing navigates and Meta
     heard nothing at all before v1.5.0. These readers gather the visitor's
     current state and send ONE Search event once they stop adjusting it.

     A dropdown counts as a filter only when it is off its first option, which
     on both pages is the "Any ..." / "All ..." default. */

  function selectedText(sel) {
    try {
      if (!sel || sel.selectedIndex < 0) { return ''; }
      var o = sel.options[sel.selectedIndex];
      return o ? String(o.text).trim() : '';
    } catch (e) { return ''; }
  }

  // Marketplace option labels carry a live stock count: "ELECTRIC VAN (3)".
  // That number moves as vehicles sell, so leaving it in would turn one
  // category into a new string every week and fragment the audience built
  // from it. Only a trailing "(digits)" is removed - a name that genuinely
  // ends in brackets is left alone.
  function stripCount(s) {
    var t = String(s == null ? '' : s).trim();
    if (t.charAt(t.length - 1) !== ')') { return t; }
    var open = t.lastIndexOf(' (');
    if (open < 0) { return t; }
    var inner = t.substring(open + 2, t.length - 1);
    if (!inner.length) { return t; }
    for (var i = 0; i < inner.length; i++) {
      var c = inner.charAt(i);
      if (c < '0' || c > '9') { return t; }
    }
    return t.substring(0, open);
  }

  function activeSelectValue(sel) {
    try {
      if (!sel || sel.selectedIndex <= 0) { return ''; }
      return stripCount(selectedText(sel));
    } catch (e) { return ''; }
  }

  function countFrom(selector) {
    var t = txt(document, selector);
    return t ? money(t) : 0;
  }

  function readUsedSearchState() {
    var input = document.querySelector('.t8-search');
    var state = {
      search_string: (input && input.value) ? String(input.value).trim() : '',
      filters: [],
      results: countFrom('.t8-count-pill')
    };
    var boxes = document.querySelectorAll('.t8-dropdown-box');
    for (var i = 0; i < boxes.length; i++) {
      var sel = boxes[i].querySelector('.t8-select');
      var v = activeSelectValue(sel);
      if (!v) { continue; }
      // The label renders uppercase via CSS but reads back as "Category",
      // so it is upper-cased here to match the new-vehicle page's labels.
      var lbl = up(txt(boxes[i], '.t8-filter-lbl')) || 'FILTER';
      state.filters.push(lbl + ': ' + v);
      if (has(lbl, 'CATEGORY')) { state.content_category = v; }
    }
    return state;
  }

  // The new-vehicle page has six dropdowns behind its Filters panel, keyed by
  // an id prefix: att, fuel, min, max, sort, cat. Two are deliberately ignored.
  //   cat  - repeats the category the chips already report, so counting it
  //          sends the same choice twice (measured: "CATEGORY: 10FT TRUCK |
  //          ELECTRIC | 10FT TRUCK").
  //   sort - re-ordering results is not searching for something, and letting
  //          it through would fire a Search event on its own.
  function newFilterLabel(key) {
    if (key === 'att')  { return 'ATTACHMENT'; }
    if (key === 'fuel') { return 'FUEL'; }
    if (key === 'min')  { return 'MIN PRICE'; }
    if (key === 'max')  { return 'MAX PRICE'; }
    return '';
  }

  function newFilterKey(el) {
    try {
      var rest = String(el.id).substring(6); // strip the leading 'abl-f-'
      var dash = rest.indexOf('-');
      return dash > -1 ? rest.substring(0, dash) : rest;
    } catch (e) { return ''; }
  }

  function readNewSearchState() {
    var state = { search_string: '', filters: [], results: countFrom('.abl-count-pill') };

    var inputs = document.querySelectorAll('input');
    for (var i = 0; i < inputs.length; i++) {
      if (idStartsWith(inputs[i], 'abl-f-search')) {
        state.search_string = inputs[i].value ? String(inputs[i].value).trim() : '';
        break;
      }
    }

    var chip = document.querySelector('.abl-chip-btn.active');
    if (chip) {
      var cat = chip.getAttribute('data-cat');
      if (cat) {
        state.content_category = cat;
        state.filters.push('CATEGORY: ' + cat);
      }
    }

    var sels = document.querySelectorAll('select');
    for (var s = 0; s < sels.length; s++) {
      if (!idStartsWith(sels[s], 'abl-f-')) { continue; }
      var lbl = newFilterLabel(newFilterKey(sels[s]));
      if (!lbl) { continue; }
      var v = activeSelectValue(sels[s]);
      if (v) { state.filters.push(lbl + ': ' + v); }
    }

    return state;
  }

  function searchPage() {
    var p = window.location.pathname;
    if (p.indexOf('car-marketplace') > -1) { return 'used'; }
    if (p.indexOf('new-commercial-vehicles') > -1) { return 'new'; }
    return '';
  }

  function readSearchState() {
    var which = searchPage();
    if (which === 'used') { return readUsedSearchState(); }
    if (which === 'new')  { return readNewSearchState(); }
    return null;
  }

  API.readSearch = readSearchState;

  var searchTimer = null;

  function queueSearch() {
    var which = searchPage();
    if (!which) { return; }
    if (searchTimer) { window.clearTimeout(searchTimer); }
    searchTimer = window.setTimeout(function () {
      try {
        var st = readSearchState();
        if (!st) { return; }
        // Nothing typed and every dropdown still on its default: not a search.
        if (!st.search_string && st.filters.length === 0) { return; }
        var joined = st.filters.join(' | ');
        fire('Search', {
          content_type:     'vehicle',
          content_name:     st.search_string || joined,
          content_category: st.content_category || (st.filters.length ? st.filters[0] : 'All'),
          search_string:    st.search_string,
          filters:          joined,
          results:          st.results,
          vehicle_condition: which === 'used' ? 'Used' : 'New',
          source_page:      which === 'used' ? 'car-marketplace' : 'new-commercial-vehicles',
          currency:         'SGD'
        });
      } catch (e) {}
    }, SEARCH_SETTLE_MS);
  }

  /* ---------- click capture ---------- */

  document.addEventListener('click', function (ev) {
    try {
      var target = ev.target;
      if (!target || !target.closest) { return; }

      var card = target.closest('.abl-card, .t8-card');
      if (card) {
        var data = readCard(card);
        if (data) {
          fire('VehicleView', data);
          // Opening WhatsApp from a card is reaching out about that vehicle.
          // It is a Contact, not a completed enquiry - see v1.5.0 note above.
          if (target.closest('.abl-btn-wa, .t8-wa')) { fire('Contact', data); }
        }
        return;
      }

      // Category chips on the new-vehicle page are buttons, not selects.
      if (target.closest('.abl-chip-btn')) { queueSearch(); return; }

      var waEl = target.closest('[onclick]');
      if (!waEl) { return; }
      var intent = ablwaIntent(waEl);
      if (!intent) { return; }

      if (intent.kind === 'lead') { fire('Lead', servicePayload(intent.service, coeContext())); }
      else if (intent.kind === 'contact') { fire('Contact', servicePayload(intent.service, coeContext())); }
      else { fire('ServiceInterest', servicePayload(intent.service)); }
    } catch (e) {}
  }, true);

  /* ---------- typing in either search box ---------- */

  document.addEventListener('input', function (ev) {
    try {
      var t = ev.target;
      if (!t) { return; }
      if (classHas(t, 't8-search')) { queueSearch(); return; }
      if (idStartsWith(t, 'abl-f-search')) { queueSearch(); }
    } catch (e) {}
  }, true);

  /* ---------- dropdowns: filters, and the COE calculator ----------
     COE is debounced longer, so picking a category and then a term produces
     ONE event carrying both instead of an early partial one. */

  var coeTimer = null;

  document.addEventListener('change', function (ev) {
    try {
      var t = ev.target;
      if (!t) { return; }

      if (classHas(t, 't8-select')) { queueSearch(); return; }
      if (idStartsWith(t, 'abl-f-')) { queueSearch(); return; }

      if (!t.id) { return; }
      if (t.id !== 'coe_cat' && t.id !== 'coe_term' && t.id !== 'coe_expiry') { return; }

      if (coeTimer) { window.clearTimeout(coeTimer); }
      coeTimer = window.setTimeout(function () {
        try {
          var ctx = coeContext();
          if (!ctx.coe_category && !ctx.coe_term) { return; }
          fire('COERenewalInterest', servicePayload('COE Renewal Loan', ctx));
        } catch (e) {}
      }, COE_SETTLE_MS);
    } catch (e) {}
  }, true);

  /* ---------- service pages ---------- */

  function servicePageIntent() {
    try {
      var p = window.location.pathname;
      if (p.indexOf('motor-insurance') > -1) { fire('ServiceInterest', servicePayload('Motor Insurance')); }
      else if (p.indexOf('coe-renewal') > -1) { fire('ServiceInterest', servicePayload('COE Renewal Loan')); }
    } catch (e) {}
  }

  /* ---------- detail view opened straight from a URL ---------- */

  function fireFromUrl() {
    try {
      var search = window.location.search || '';
      if (search.indexOf('v=') < 0) { return false; }
      var wanted = slugOf(search);
      if (!wanted) { return false; }

      var links = document.querySelectorAll('.abl-btn-info');
      for (var n = 0; n < links.length; n++) {
        if (slugOf(links[n].getAttribute('href')) === wanted) {
          var card = links[n].closest('.abl-card');
          if (card) { fire('VehicleView', readCard(card)); return true; }
        }
      }
      return false;
    } catch (e) { return false; }
  }

  /* ---------- start ---------- */

  servicePageIntent();

  if (window.location.pathname.indexOf('car-marketplace') > -1) { loadUsedIndex(); }

  // Cards render only after the backend responds, so poll briefly.
  var waits = 0;
  (function waitForCards() {
    try {
      if (document.querySelector('.t8-card')) { loadUsedIndex(); }
      if (document.querySelector('.abl-card, .t8-card')) { fireFromUrl(); return; }
      waits = waits + 1;
      if (waits > 40) { return; }
      window.setTimeout(waitForCards, 500);
    } catch (e) {}
  })();

  window.addEventListener('popstate', function () { fireFromUrl(); });
})();
